import { useCallback, useMemo, useState } from "react";
import { Box } from "@mui/material";
import { DropdownOption } from "@components/Dropdown/Dropdown";
import { onErrorShow } from "@utils/onErrorShow";
import { onInfoShow } from "@utils/onInfoShow";
import {
  CustomFieldsClient,
  CustomFieldEntityType,
  ICustomFieldDefinition,
} from "@Clients/CustomFieldsClient";
import { PreviewField } from "./PreviewField";
import { Header } from "./Header";
import { CustomFieldForm } from "./CustomFieldForm";
import { ExistingCustomFields } from "./ExistingCustomFields";

type CustomFieldsManagerProps = {
  organizationId: string;
  entityType: CustomFieldEntityType;
  entityLabel: string;
  customFields: ICustomFieldDefinition[];
  isLoading: boolean;
  onRefresh: () => Promise<void>;
};

const fieldTypeOptions: DropdownOption[] = [
  { value: "text", label: "Text" },
  { value: "number", label: "Number" },
  { value: "date", label: "Date" },
  { value: "boolean", label: "Boolean" },
  { value: "select", label: "Select (Dropdown)" },
  { value: "textarea", label: "Textarea" },
];

const normalizeKey = (value: string) =>
  value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

const normalizeOptionValue = (value: string) =>
  value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

const initialNewFieldState: Partial<ICustomFieldDefinition> = {
  label: "",
  type: "text",
  required: false,
  display_order: 0,
  active: true,
  placeholder: "",
  help_text: "",
  default_value: "",
  validation_rules: {},
  field_options: [],
};

export const CustomFieldsManager = ({
  organizationId,
  entityType,
  entityLabel,
  customFields,
  isLoading,
  onRefresh,
}: CustomFieldsManagerProps) => {
  const [isSaving, setIsSaving] = useState(false);
  const [editingFieldId, setEditingFieldId] = useState<string | null>(null);
  const [editingField, setEditingField] = useState<
    Partial<ICustomFieldDefinition>
  >({});
  const [newField, setNewField] = useState<Partial<ICustomFieldDefinition>>(
    initialNewFieldState
  );

  const derivedFieldKey = useMemo(
    () => (newField.label ? normalizeKey(newField.label) : ""),
    [newField.label]
  );

  const normalizedOptions = useMemo(() => {
    const options = newField.field_options || [];
    return options
      .map((option) => {
        const label = option.label?.trim() || "";
        const value = normalizeOptionValue(label);
        if (!label || !value) return null;
        return { label, value };
      })
      .filter(Boolean) as Array<{ label: string; value: string }>;
  }, [newField.field_options]);

  const duplicateOptionValues = useMemo(() => {
    const counts = new Map<string, number>();
    normalizedOptions.forEach((option) => {
      counts.set(option.value, (counts.get(option.value) || 0) + 1);
    });
    return new Set(
      Array.from(counts.entries())
        .filter(([, count]) => count > 1)
        .map(([value]) => value)
    );
  }, [normalizedOptions]);

  const previewField = useMemo(() => {
    return {
      label: newField.label?.trim() || "Field Label",
      type: newField.type || "text",
      placeholder: newField.placeholder?.trim() || "",
      required: newField.required || false,
      help_text: newField.help_text?.trim() || "",
      field_options: normalizedOptions,
    };
  }, [newField, normalizedOptions]);

  const handleAddField = useCallback(async () => {
    if (!newField.label?.trim()) {
      onErrorShow("Field label is required");
      return;
    }
    if (!derivedFieldKey) {
      onErrorShow("Field label must contain letters or numbers");
      return;
    }
    if (newField.type === "select" && normalizedOptions.length === 0) {
      onErrorShow("Add at least one dropdown option");
      return;
    }
    if (newField.type === "select") {
      const optionValues = normalizedOptions.map((option) => option.value);
      const uniqueOptionValues = new Set(optionValues);
      if (uniqueOptionValues.size !== optionValues.length) {
        onErrorShow("Dropdown options must be unique");
        return;
      }
    }

    const maxOrder = customFields.reduce(
      (max, field) => Math.max(max, field.display_order),
      0
    );

    setIsSaving(true);
    try {
      await CustomFieldsClient.createDefinition({
        organization_id: organizationId,
        entity_type: entityType,
        name: derivedFieldKey,
        label: newField.label.trim(),
        type: newField.type || "text",
        required: newField.required || false,
        display_order: newField.display_order || maxOrder + 1,
        active: true,
        placeholder: newField.placeholder || undefined,
        help_text: newField.help_text || undefined,
        default_value: newField.default_value || undefined,
        validation_rules: newField.validation_rules || {},
        field_options: newField.type === "select" ? normalizedOptions : [],
      });

      onInfoShow("Custom field added successfully");
      setNewField(initialNewFieldState);
      await onRefresh();
    } catch (error) {
      console.error("Error adding custom field:", error);
      onErrorShow("Failed to add custom field");
    } finally {
      setIsSaving(false);
    }
  }, [
    customFields,
    derivedFieldKey,
    entityType,
    newField,
    normalizedOptions,
    onRefresh,
    organizationId,
  ]);

  const handleDeleteField = useCallback(
    async (id: string) => {
      try {
        await CustomFieldsClient.deleteDefinition(id);
        onInfoShow("Custom field deleted successfully");
        await onRefresh();
      } catch (error) {
        console.error("Error deleting custom field:", error);
        onErrorShow("Failed to delete custom field");
      }
    },
    [onRefresh]
  );

  const handleStartEdit = useCallback((field: ICustomFieldDefinition) => {
    setEditingFieldId(field.id);
    setEditingField({
      id: field.id,
      label: field.label,
      placeholder: field.placeholder || "",
      help_text: field.help_text || "",
      required: field.required,
      display_order: field.display_order,
    });
  }, []);

  const handleCancelEdit = useCallback(() => {
    setEditingFieldId(null);
    setEditingField({});
  }, []);

  const handleSaveEdit = useCallback(async () => {
    if (!editingFieldId) return;
    const fieldToUpdate = customFields.find(
      (field) => field.id === editingFieldId
    );
    if (!fieldToUpdate) return;

    setIsSaving(true);
    try {
      await CustomFieldsClient.updateDefinition({
        ...fieldToUpdate,
        label: editingField.label || fieldToUpdate.label,
        placeholder: editingField.placeholder || undefined,
        help_text: editingField.help_text || undefined,
        required: editingField.required ?? fieldToUpdate.required,
        display_order: editingField.display_order ?? fieldToUpdate.display_order,
      });

      onInfoShow("Custom field updated successfully");
      setEditingFieldId(null);
      setEditingField({});
      await onRefresh();
    } catch (error) {
      console.error("Error updating custom field:", error);
      onErrorShow("Failed to update custom field");
    } finally {
      setIsSaving(false);
    }
  }, [customFields, editingField, editingFieldId, onRefresh]);

  const handleAddOption = useCallback(() => {
    setNewField((prev) => ({
      ...prev,
      field_options: [...(prev.field_options || []), { label: "", value: "" }],
    }));
  }, []);

  const handleUpdateOption = useCallback((index: number, value: string) => {
    setNewField((prev) => {
      const options = [...(prev.field_options || [])];
      options[index] = { ...options[index], label: value };
      return { ...prev, field_options: options };
    });
  }, []);

  const handleRemoveOption = useCallback((index: number) => {
    setNewField((prev) => {
      const options = [...(prev.field_options || [])];
      options.splice(index, 1);
      return { ...prev, field_options: options };
    });
  }, []);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 3,
      }}
    >
      <Header entityLabel={entityLabel} />
      <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, gap: 3 }}>
        <CustomFieldForm
          duplicateOptionValues={duplicateOptionValues}
          normalizeOptionValue={normalizeOptionValue}
          newField={newField}
          setNewField={setNewField}
          fieldTypeOptions={fieldTypeOptions}
          isSaving={isSaving}
          handleAddField={handleAddField}
          handleAddOption={handleAddOption}
          handleRemoveOption={handleRemoveOption}
          handleUpdateOption={handleUpdateOption}
        />

        <PreviewField previewField={previewField} />
      </Box>
      <ExistingCustomFields
        customFields={customFields}
        isLoading={isLoading}
        isSaving={isSaving}
        handleStartEdit={handleStartEdit}
        handleCancelEdit={handleCancelEdit}
        handleSaveEdit={handleSaveEdit}
        handleDeleteField={handleDeleteField}
        editingFieldId={editingFieldId}
        editingField={editingField}
        setEditingField={setEditingField}
      />

    </Box>
  );
};
